import React from 'react';
import { motion } from 'framer-motion';

export interface OfferCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  dealCount?: number;
}

export interface HotDeal {
  id: string;
  title: string;
  discount: string;
  image: string;
  category: string;
}

export interface OfferCardProps {
  variant: 'category' | 'deal';
  category?: OfferCategory;
  deal?: HotDeal;
  onClick?: () => void;
  className?: string;
}

export const OfferCard: React.FC<OfferCardProps> = ({
  variant,
  category,
  deal,
  onClick,
  className = '',
}) => {
  if (variant === 'category' && category) {
    return (
      <motion.button
        onClick={onClick}
        className={`
          relative w-full aspect-square bg-white rounded-2xl p-6
          flex flex-col items-center justify-center
          shadow-[0_2px_8px_rgba(0,0,0,0.08)]
          hover:shadow-[0_4px_16px_rgba(0,0,0,0.12)]
          focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A054]
          transition-shadow duration-200
          ${className}
        `}
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.2 }}
      >
        {/* Deal Badge */}
        {category.dealCount && category.dealCount > 0 && (
          <div className="absolute top-3 right-3 bg-[#D4A054] text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
            {category.dealCount}+ Deals
          </div>
        )}

        {/* Icon Container */}
        <div className="w-20 h-20 rounded-full bg-[#FDF6E9] flex items-center justify-center mb-4">
          <div className="text-[#D4A054] w-16 h-16 flex items-center justify-center">
            {category.icon}
          </div>
        </div>

        {/* Label */}
        <h3 className="text-[#333333] text-base font-medium text-center">
          {category.name}
        </h3>
      </motion.button>
    );
  }

  if (variant === 'deal' && deal) {
    return (
      <motion.button
        onClick={onClick}
        className={`
          relative w-64 h-72 rounded-2xl overflow-hidden flex-shrink-0
          shadow-[0_2px_8px_rgba(0,0,0,0.08)]
          hover:shadow-[0_4px_16px_rgba(0,0,0,0.12)]
          focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A054]
          transition-shadow duration-200
          ${className}
        `}
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.2 }}
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={deal.image}
            alt={deal.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[rgba(26,26,26,0.8)] via-transparent to-transparent" />

        {/* Discount Badge */}
        <div className="absolute bottom-4 right-4 bg-[rgba(26,26,26,0.85)] text-white text-sm font-semibold px-3 py-1.5 rounded-lg">
          {deal.discount}
        </div>

        {/* Title */}
        <div className="absolute bottom-4 left-4 right-20">
          <h3 className="text-white font-medium text-lg text-shadow">
            {deal.title}
          </h3>
          <p className="text-white/70 text-sm">{deal.category}</p>
        </div>
      </motion.button>
    );
  }

  return null;
};

export default OfferCard;
